// Derived from https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document

// Save a list of named combobox actions, for future readability
const SelectActions = {
	Close: 0,
	CloseSelect: 1,
	First: 2,
	Last: 3,
	Next: 4,
	Open: 5,
	PageDown: 6,
	PageUp: 7,
	Previous: 8,
	Select: 9,
	Type: 10,
};

/*
 * Helper functions
 */

// filter an array of options against an input string
// returns an array of options that begin with the filter string, case-independent
function filterOptions( options = [], filter, exclude = [] ) {
	return options.filter( option => {
		const matches = option.toLowerCase().indexOf( filter.toLowerCase() ) === 0;
		return matches && exclude.indexOf( option ) < 0;
	} );
}

// map a key press to an action
function getActionFromKey( event, menuOpen ) {
	const { key, altKey, ctrlKey, metaKey } = event;
	const openKeys = [ 'ArrowDown', 'ArrowUp', 'Enter', ' ' ]; // all keys that will do the default open action

	// handle opening when closed
	if ( ! menuOpen && openKeys.includes( key ) ) {
		return SelectActions.Open;
	}

	// home and end move the selected option when open or closed
	if ( key === 'Home' ) {
		return SelectActions.First;
	}
	if ( key === 'End' ) {
		return SelectActions.Last;
	}

	// handle typing characters when open or closed
	if (
		key === 'Backspace' ||
		key === 'Clear' ||
		( key.length === 1 && key !== ' ' && ! altKey && ! ctrlKey && ! metaKey )
	) {
		return SelectActions.Type;
	}

	// handle keys when open
	if ( menuOpen ) {
		if ( key === 'ArrowUp' && altKey ) {
			return SelectActions.CloseSelect;
		} else
		if ( key === 'ArrowDown' && ! altKey ) {
			return SelectActions.Next;
		} else
		if ( key === 'ArrowUp' ) {
			return SelectActions.Previous;
		} else
		if ( key === 'PageUp' ) {
			return SelectActions.PageUp;
		} else
		if ( key === 'PageDown' ) {
			return SelectActions.PageDown;
		} else
		if ( key === 'Escape' ) {
			return SelectActions.Close;
		} else
		if ( key === 'Enter' || key === ' ' ) {
			return SelectActions.CloseSelect;
		}
	}
}

// return the index of an option from an array of options, based on a search string
// if the filter is multiple iterations of the same letter (e.g "aaa"), then cycle through first-letter matches
function getIndexByLetter( options, filter, startIndex = 0 ) {
	const orderedOptions = [
		...options.slice( startIndex ),
		...options.slice( 0, startIndex ),
	];
	const firstMatch = filterOptions( orderedOptions, filter )[ 0 ];
	const allSameLetter = array => array.every( letter => letter === array[ 0 ] );

	// first check if there is an exact match for the typed string
	// else if the same letter is being repeated, cycle through first-letter matches
	if ( firstMatch ) {
		return options.indexOf( firstMatch );
	} else if ( allSameLetter( filter.split( '' ) ) ) {
		const matches = filterOptions( orderedOptions, filter[ 0 ] );
		return options.indexOf( matches[ 0 ] );
	}

	// if no matches, return -1
	return -1;
}

// get an updated option index after performing an action
function getUpdatedIndex( currentIndex, maxIndex, action ) {
	const pageSize = 10; // used for pageup/pagedown

	switch ( action ) {
		case SelectActions.First:
			return 0;

		case SelectActions.Last:
			return maxIndex;

		case SelectActions.Previous:
			return Math.max( 0, currentIndex - 1 );

		case SelectActions.Next:
			return Math.min( maxIndex, currentIndex + 1 );

		case SelectActions.PageUp:
			return Math.max( 0, currentIndex - pageSize );

		case SelectActions.PageDown:
			return Math.min( maxIndex, currentIndex + pageSize );

		default:
			return currentIndex;
	}
}

// check if element is visible in browser view port
function isElementInView( element ) {
	const bounding = element.getBoundingClientRect();

	return (
		bounding.top >= 0 &&
		bounding.left >= 0 &&
		bounding.bottom <=
			( window.innerHeight || document.documentElement.clientHeight ) &&
		bounding.right <=
			( window.innerWidth || document.documentElement.clientWidth )
	);
}

// check if an element is currently scrollable
function isScrollable( element ) {
	return element && element.clientHeight < element.scrollHeight;
}

// ensure a given child element is within the parent's visible scroll area
// if the child is not visible, scroll the parent
function maintainScrollVisibility( activeElement, scrollParent ) {
	const { offsetHeight, offsetTop } = activeElement;
	const { offsetHeight: parentOffsetHeight, scrollTop } = scrollParent;

	const isAbove = offsetTop < scrollTop;
	const isBelow = offsetTop + offsetHeight > scrollTop + parentOffsetHeight;

	if ( isAbove ) {
		scrollParent.scrollTo( 0, offsetTop );
	} else if ( isBelow ) {
		scrollParent.scrollTo( 0, offsetTop - parentOffsetHeight + offsetHeight );
	}
}

/*
 * Select Component
 * Accepts a combobox element and an array of string options
 */
let instanceCount = 1;
class Combobox {
	constructor( container ) {
		this.selectElement = container.querySelector( 'select' );
		this.selectElement.setAttribute( 'aria-hidden', this.selectElement.hidden = true );

		const label = container.querySelector( 'label' );
		if ( ! label.id ) {
			label.id = `combobox${ instanceCount }-label`;
		}

		const wrapper = document.createElement( 'div' );
		wrapper.setAttribute( 'class', 'combobox js-select' );
		this.wrapperElement = wrapper;

		const combobox = document.createElement( 'div' );
		combobox.setAttribute( 'class', 'combobox__input' );
		combobox.setAttribute( 'role', 'combobox' );
		combobox.setAttribute( 'id', `combobox${ instanceCount }` );
		combobox.setAttribute( 'aria-labelledby', label.id );
		combobox.setAttribute( 'aria-haspopup', 'listbox' );
		combobox.setAttribute( 'aria-controls', `listbox${ instanceCount }` );
		combobox.setAttribute( 'aria-expanded', 'false' );
		combobox.setAttribute( 'tabindex', '0' );
		this.comboboxElement = combobox;

		const listbox = document.createElement( 'div' );
		listbox.setAttribute( 'class', 'combobox__menu' );
		listbox.setAttribute( 'role', 'listbox' );
		listbox.setAttribute( 'id', `listbox${ instanceCount }` );
		listbox.setAttribute( 'aria-labelledby', label.id );
		listbox.setAttribute( 'tabindex', '-1' );
		this.listboxElement = listbox;

		label.htmlFor = combobox.id;
		wrapper.append( combobox );
		wrapper.append( listbox );
		this.selectElement.parentNode.insertBefore( wrapper, this.selectElement );

		this.optionStrings = [];
		this.optionElements = [];
		this.selectElement.querySelectorAll( 'option' ).forEach( ( option, index ) => {
			this.optionStrings.push( option.textContent );

			const optionElement = this.createOption( option, index );
			this.optionElements.push( optionElement );
			this.listboxElement.appendChild( optionElement );
		} );

		// Initialize
		this.activeIndex = 0;
		this.isOpen = false;
		this.searchString = '';
		this.searchTimeout = null;
		this.ignoreChangeEvent = false;
		this.selectOption( this.selectElement.selectedIndex );

		// add event listeners
		this.comboboxElement.addEventListener( 'blur', this.onComboboxBlur.bind( this ) );
		this.comboboxElement.addEventListener( 'click', this.onComboboxClick.bind( this ) );
		this.comboboxElement.addEventListener( 'keydown', this.onComboboxKeyDown.bind( this ) );
		this.selectElement.addEventListener( 'change', () => {
			if ( ! this.ignoreChangeEvent ) {
				this.selectOption( this.selectElement.selectedIndex );
			}
		} );

		instanceCount++;
	}

	createOption( option, index ) {
		const optionElement = document.createElement( 'div' );
		optionElement.setAttribute( 'role', 'option' );
		optionElement.id = `${ this.listboxElement.id }-option${ index }`;
		optionElement.className = 'combobox__option';
		optionElement.innerText = option.innerText;
		optionElement.dataset.value = option.value;

		optionElement.addEventListener( 'click', event => {
			event.stopPropagation();
			this.onOptionClick( index );
		} );

		optionElement.addEventListener( 'mousedown', this.onOptionMouseDown.bind( this ) );

		return optionElement;
	}

	getSearchString( char ) {
		// reset typing timeout and start new timeout
		// this allows us to make multiple-letter matches, like a native select
		if ( typeof this.searchTimeout === 'number' ) {
			window.clearTimeout( this.searchTimeout );
		}

		this.searchTimeout = window.setTimeout( () => {
			this.searchString = '';
		}, 500 );

		// add most recent letter to saved search string
		this.searchString += char;
		return this.searchString;
	}

	onComboboxBlur() {
		// do not do blur action if ignoreBlur flag has been set
		if ( this.ignoreBlur ) {
			this.ignoreBlur = false;
			return;
		}

		// select current option and close
		if ( this.isOpen ) {
			this.selectOption( this.activeIndex, 'silent' );
			this.updateMenuState( false, false );
		}
	}

	onComboboxClick() {
		this.updateMenuState( ! this.isOpen, false );
	}

	onComboboxKeyDown( event ) {
		const { key } = event;
		const max = this.optionElements.length - 1;

		const action = getActionFromKey( event, this.isOpen );

		switch ( action ) {
			case SelectActions.Last:
			case SelectActions.First:
				this.updateMenuState( true );

			// intentional fallthrough
			case SelectActions.Next:
			case SelectActions.Previous:
			case SelectActions.PageUp:
			case SelectActions.PageDown:
				event.preventDefault();
				return this.onOptionChange(
					getUpdatedIndex( this.activeIndex, max, action ),
				);

			case SelectActions.CloseSelect:
				event.preventDefault();
				this.selectOption( this.activeIndex );

			// intentional fallthrough
			case SelectActions.Close:
				event.preventDefault();
				return this.updateMenuState( false );

			case SelectActions.Type:
				return this.onComboboxType( key );

			case SelectActions.Open:
				event.preventDefault();
				return this.updateMenuState( true );
		}
	}

	onComboboxType( letter ) {
		// open the listbox if it is closed
		this.updateMenuState( true );

		// find the index of the first matching option
		const searchString = this.getSearchString( letter );
		const searchIndex = getIndexByLetter(
			this.optionStrings,
			searchString,
			this.activeIndex + 1,
		);

		// if a match was found, go to it
		// if no matches, clear the timeout and search string
		if ( searchIndex >= 0 ) {
			this.onOptionChange( searchIndex );
		} else {
			window.clearTimeout( this.searchTimeout );
			this.searchString = '';
		}
	}

	onOptionChange( index ) {
		// update state
		this.activeIndex = index;

		// update current option
		this.highlightOption( index );

		// update aria-activedescendant
		this.comboboxElement.setAttribute( 'aria-activedescendant', this.optionElements[ index ].id );

		// ensure the new option is visible on screen
		if ( isScrollable( this.listboxElement ) ) {
			maintainScrollVisibility( this.optionElements[ index ], this.listboxElement );
		}

		// ensure the new option is in view
		if ( ! isElementInView( this.optionElements[ index ] ) ) {
			this.optionElements[ index ].scrollIntoView( { block: 'nearest' } );
		}
	}

	onOptionClick( index ) {
		this.onOptionChange( index );
		this.selectOption( index );
		this.updateMenuState( false );
	}

	onOptionMouseDown() {
		// Clicking an option will cause a blur event,
		// but we don't want to perform the default keyboard blur action
		this.ignoreBlur = true;
	}

	highlightOption( index ) {
		this.optionElements.forEach( ( optionElement, i ) => {
			if ( i === index ) {
				optionElement.classList.add( 'is-current' );
			} else {
				optionElement.classList.remove( 'is-current' );
			}
		} );
	}

	selectOption( index, silent = false ) {
		// update state
		this.activeIndex = index;

		// update displayed value
		this.comboboxElement.textContent = this.optionElements[ index ].textContent;

		// update aria-selected
		this.optionElements.forEach( ( optionElement, i ) => {
			optionElement.setAttribute( 'aria-selected', i === index ? 'true' : 'false' );
		} );

		// ensure option is highlighted
		this.highlightOption( index );

		// update the original <select>
		this.selectElement.value = this.optionElements[ index ].dataset.value;

		if ( ! silent ) {
			// trigger change event, but ignore internally to avoid infinite loop
			this.ignoreChangeEvent = true;
			this.selectElement.dispatchEvent( new Event( 'change' ) );
			this.ignoreChangeEvent = false;
		}
	}

	updateMenuState( isOpen, callFocus = true ) {
		if ( this.isOpen === isOpen ) {
			return;
		}

		// update state
		this.isOpen = isOpen;

		// update aria-expanded and styles
		this.comboboxElement.setAttribute( 'aria-expanded', `${ isOpen }` );

		// update activedescendant
		const activeId = this.isOpen ? this.optionElements[ this.activeIndex ].id : '';
		this.comboboxElement.setAttribute( 'aria-activedescendant', activeId );

		if ( activeId === '' && ! isElementInView( this.comboboxElement ) ) {
			this.comboboxElement.scrollIntoView( { block: 'nearest' } );
		}

		// move focus back to the combobox, if needed
		if ( callFocus ) {
			this.comboboxElement.focus();
		}
	}
}

export default function setupDropdowns() {
	document.querySelectorAll( '.js-dropdown' ).forEach( widget => {
		new Combobox( widget );
	} );
}
